import { useRef, useState } from "react";
import { toast } from "sonner";
import { StickyNote, Upload, Trash2 } from "lucide-react";
import SectionCard, { inputCls } from "./SectionCard";
import { api, photoUrl, uploadFiles, formatApiError } from "../../api";

export default function NotesSection({ vehicle, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draft, setDraft] = useState("");
  const fileRef = useRef(null);
  const allGallery = vehicle.photo_gallery || [];
  const photos = allGallery.filter((g) => g.category === "notlar");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/my-vehicles/${vehicle.id}/section/notes`, { value: draft });
      onSaved(data);
      setEditing(false);
      toast.success("Notlar kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveGallery = async (g, msg) => {
    try {
      const { data } = await api.put(`/my-vehicles/${vehicle.id}/section/photo_gallery`, { value: g });
      onSaved(data);
      if (msg) toast.success(msg);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onUpload = async (files) => {
    setUploading(true);
    try {
      const paths = await uploadFiles(files);
      const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `g-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      const items = paths.map((p) => ({ id: newId(), path: p, category: "notlar" }));
      await saveGallery([...allGallery, ...items], `${items.length} fotoğraf eklendi`);
    } catch (e) { toast.error(formatApiError(e)); }
    setUploading(false);
  };

  return (
    <SectionCard testid="section-notlar" title="Notlar" Icon={StickyNote}
      desc="Ekspertiz notları ve araçla ilgili serbest notlar; metin ve fotoğraf ekleyebilirsiniz."
      editing={editing} onEdit={() => { setDraft(vehicle.notes || ""); setEditing(true); }}
      onCancel={() => setEditing(false)} onSave={save} saving={saving}>
      {!editing ? (
        vehicle.notes ? (
          <p data-testid="notlar-text" className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{vehicle.notes}</p>
        ) : (
          <p className="text-sm text-slate-500">Henüz not eklenmedi.</p>
        )
      ) : (
        <textarea data-testid="notlar-textarea" rows={10} value={draft} onChange={(e) => setDraft(e.target.value)}
          className={inputCls} placeholder="Ekspertiz notları, açıklamalar..." />
      )}

      <div className="mt-4 pt-4 border-t border-white/10">
        <p className="text-[11px] font-semibold text-slate-400 mb-2 tracking-wide">NOT FOTOĞRAFLARI</p>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
          data-testid="notlar-file-input" onChange={(e) => { onUpload([...e.target.files]); e.target.value = ""; }} />
        <button data-testid="notlar-upload-button" onClick={() => fileRef.current?.click()} disabled={uploading}
          className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl border border-dashed border-white/20 text-slate-300 text-sm hover:border-cyan-400/50 hover:text-cyan-300 transition-colors disabled:opacity-50">
          <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Fotoğraf Yükle"}
        </button>
        {photos.length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {photos.map((g) => (
              <div key={g.id} data-testid={`notlar-photo-${g.id}`} className="relative rounded-xl border border-white/10 bg-slate-950/50 overflow-hidden">
                <a href={photoUrl(g.path)} target="_blank" rel="noreferrer">
                  <img src={photoUrl(g.path)} alt="" className="w-full h-28 object-cover" />
                </a>
                <button data-testid={`notlar-photo-delete-${g.id}`}
                  onClick={() => saveGallery(allGallery.filter((x) => x.id !== g.id), "Fotoğraf silindi")}
                  className="absolute top-1.5 right-1.5 p-1.5 rounded-lg bg-slate-950/80 border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/40 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionCard>
  );
}
